import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { analyticsAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RefreshCw, LayoutDashboard, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import { OpsOverview } from '../components/dashboard/OpsOverview';
import { OpsCalendar } from '../components/dashboard/OpsCalendar';
import { InvoicesDueSoonWidget } from '../components/dashboard/InvoicesDueSoonWidget';

export const DashboardPage = () => {
  const { canViewAllPartners } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const ops = await analyticsAPI.getOpsOverview();
      setData(ops);
    } catch (error) {
      console.error('Failed to load ops overview:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Training Delivery Dashboard</h1>
          <p className="text-muted-foreground">
            {canViewAllPartners() ? 'Operations command center across all delivery types' : 'Your training operations overview'}
            {data?.as_of ? ` · as of ${new Date(data.as_of + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
          </p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-dashboard">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading || !data ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <Card key={i}><CardContent className="pt-6"><div className="h-16 bg-muted animate-pulse rounded" /></CardContent></Card>
            ))}
          </div>
          <Card><CardContent className="pt-6"><div className="h-40 bg-muted animate-pulse rounded" /></CardContent></Card>
        </div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="space-y-5">
          <TabsList data-testid="dashboard-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">
              <CalendarRange className="w-4 h-4 mr-2" /> Calendar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-5">
            <OpsOverview data={data} onJumpCalendar={() => setTab('calendar')} />
            <InvoicesDueSoonWidget />
          </TabsContent>

          <TabsContent value="calendar">
            <OpsCalendar events={data.calendar_events} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default DashboardPage;
